import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import {
  FlexColumn,
  RadioGroup,
  SearchField,
} from '@elementary/common-ui/components';
import {
  fontSize,
  fontWeight,
  lineHeight,
  space,
} from '@elementary/common-ui/style/utils';
import MergeIncidentCard, {
  type MergeIncidentCardItem,
} from './MergeIncidentCard';

const SectionLabel = styled(Typography)`
  font-size: ${fontSize('xs')};
  font-weight: ${fontWeight('semibold')};
  letter-spacing: 0.05em;
  line-height: ${lineHeight('relaxed')};
  color: ${({ theme }) => theme.palette.content.tertiary};
  text-transform: uppercase;
`;

const HelperText = styled(Typography)`
  font-size: ${fontSize('base')};
  line-height: ${lineHeight('comfortable')};
  color: ${({ theme }) => theme.palette.content.secondary};
`;

const SelectorRoot = styled(FlexColumn)`
  min-width: 0;
  min-height: 0;
  flex: 1 1 0;
`;

const Header = styled(FlexColumn)`
  gap: ${space('md')};
  padding: ${space('xxl', 'xxxl', 'lg')};
  flex: 0 0 auto;
`;

const CardsDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.palette.surface.hover};
  flex: 0 0 auto;
`;

const CardsGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: ${space('md')};
  min-width: 0;
  min-height: 0;
  width: 100%;
  flex: 1;
  overflow-y: auto;
  padding: ${space('lg', 'xxxl', 'xxl')};
`;

type Props = {
  incidents: MergeIncidentCardItem[];
  primaryId: string;
  onPrimaryChange: (id: string) => void;
};

const MergeIncidentSelector = ({
  incidents,
  primaryId,
  onPrimaryChange,
}: Props) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return incidents;
    const q = search.trim().toLowerCase();
    return incidents.filter((i) => i.title.toLowerCase().includes(q));
  }, [incidents, search]);

  return (
    <SelectorRoot>
      <Header>
        <FlexColumn sx={{ gap: 0.5 }}>
          <SectionLabel>SELECT PRIMARY INCIDENT</SectionLabel>
          <HelperText>
            The primary incident remains after merging. Others are merged into
            it.
          </HelperText>
        </FlexColumn>
        <SearchField
          placeholder="Filter incidents..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: '100%', maxWidth: 360 }}
        />
      </Header>
      <CardsDivider />
      <CardsGroup value={primaryId} onChange={(_, v) => onPrimaryChange(v)}>
        {filtered.map((incident) => (
          <MergeIncidentCard
            key={incident.id}
            incident={incident}
            selected={incident.id === primaryId}
            onSelect={onPrimaryChange}
          />
        ))}
      </CardsGroup>
    </SelectorRoot>
  );
};

export default MergeIncidentSelector;
